import { useConfirmModal } from '@/components/ConfirmModalProvider';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/Command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/Popover';
import { Separator } from '@/components/ui/Separator';
import {
  AspNetUsers,
  AuditLogByEntityQuery,
  AuditLogInsertInput,
  UserDeviceLink,
  UserDeviceLinkInsertInput,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { AuditLogAction, AuditLogEntityType } from '@/models/audit-log.models';
import { FeatureName, Role } from '@/models/role.models';
import { createAuditLogInput } from '@/utils/audit-log.function';
import hasPermission from '@/utils/permission.function';
import {
  QueryObserverResult,
  RefetchOptions,
  useMutation,
  useQuery,
} from '@tanstack/react-query';
import { ChevronsUpDown, X } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import {
  createUserDeviceLinkMutation,
  deleteUserDeviceLinkMutation,
} from '../../graphql/mutation';
import {
  userByRoleQuery,
  userDeviceLinksByDeviceQuery,
} from '../../graphql/query';

interface DeviceAssignedUserTabProps {
  deviceId: string;
  onValueChange?: (value: string) => void;
  refetchAuditLog: (
    options?: RefetchOptions,
  ) => Promise<QueryObserverResult<AuditLogByEntityQuery, Error>>;
}

export const DeviceAssignedUserTab: React.FC<DeviceAssignedUserTabProps> = ({
  deviceId,
  refetchAuditLog,
}: DeviceAssignedUserTabProps) => {
  const [open, setOpen] = React.useState(false);
  const { openModal } = useConfirmModal();

  const { data: usersData } = useQuery({
    queryKey: ['UserByRole'],
    queryFn: () =>
      execute({
        query: userByRoleQuery,
        variables: {
          role: Role.INSTALLER,
        },
      }),
    select: (data) => data,
    retry: false,
    staleTime: 5000,
    enabled: hasPermission(FeatureName.VIEW_API_KEYS),
  });

  const { data, refetch } = useQuery({
    queryKey: ['UserDeviceLinksByDevice', deviceId],
    queryFn: () =>
      execute({
        query: userDeviceLinksByDeviceQuery,
        variables: {
          deviceId: deviceId,
        },
      }),
    select: (data) => data,
    retry: false,
    staleTime: 5000,
    enabled: hasPermission(FeatureName.VIEW_ASSIGNED_USERS),
  });

  const { mutateAsync: createUserDeviceLink } = useMutation({
    mutationKey: ['CreateUserDeviceLink'],
    mutationFn: ({
      input,
      auditLogInput,
    }: {
      input: UserDeviceLinkInsertInput;
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: createUserDeviceLinkMutation,
        variables: {
          input,
          auditLogInput,
        },
      });
    },
    onSuccess: async () => {
      await refetch();
      await refetchAuditLog();
      toast.success('User assigned successfully!', {
        duration: 3000,
      });
    },
  });

  const { mutateAsync: deleteUserDeviceLink } = useMutation({
    mutationKey: ['DeleteUserDeviceLink'],
    mutationFn: ({
      userDeviceLinkId,
      auditLogInput,
    }: {
      userDeviceLinkId: string;
      auditLogInput: AuditLogInsertInput;
    }) => {
      return execute({
        query: deleteUserDeviceLinkMutation,
        variables: {
          id: userDeviceLinkId,
          auditLogInput,
        },
      });
    },

    onSuccess: async () => {
      await refetch();
      await refetchAuditLog();
      toast.success('User removed successfully!', {
        duration: 3000,
      });
    },
  });

  const userDeviceLinks = (data?.userDeviceLink ?? []) as UserDeviceLink[];
  const users = (usersData?.aspNetUsers ?? []) as AspNetUsers[];

  const assignUserToDevice = async (userId: string) => {
    const timestamp = new Date().toISOString();
    const userDeviceLinkId = uuid();

    await createUserDeviceLink({
      input: {
        createdAt: timestamp,
        updatedAt: timestamp,
        deviceId: deviceId,
        id: userDeviceLinkId,
        userId: userId,
      },
      auditLogInput: createAuditLogInput({
        action: AuditLogAction.ASSIGN_USER_TO_DEVICE,
        entities: [
          {
            id: userId,
            entity: AuditLogEntityType.USER,
          },
          {
            id: deviceId,
            entity: AuditLogEntityType.DEVICE,
          },
        ],
      }),
    });
  };

  const removeUser = async (userDeviceLinkId: string) => {
    const userId = userDeviceLinks.find(
      (link) => link.id === userDeviceLinkId,
    )?.userId;

    await deleteUserDeviceLink({
      userDeviceLinkId,
      auditLogInput: createAuditLogInput({
        action: AuditLogAction.UNASSIGN_USER_FROM_DEVICE,
        entities: [
          {
            id: deviceId,
            entity: AuditLogEntityType.DEVICE,
          },
          {
            id: userId as string,
            entity: AuditLogEntityType.USER,
          },
        ],
      }),
    });
  };

  const confirmAssignUserToDevice = (userId: string) => {
    openModal(
      {
        message: 'Are you sure you want to grant access to this user?',
      },
      async () => {
        await assignUserToDevice(userId);
      },
    );
  };

  const selectedIds = userDeviceLinks?.map((link) => link.userId) ?? [];
  const options =
    users
      ?.filter((user) => !selectedIds.includes(user.id))
      .map((user) => {
        return {
          label: user.userName as string,
          value: user.id,
        };
      }) ?? [];

  const canEdit = hasPermission(FeatureName.ASSIGN_USERS_TO_DEVICE);
  return (
    <Card className="gap-4">
      <CardHeader>
        <CardTitle>Assigned Users</CardTitle>
        <CardDescription>Users with access</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!canEdit ? (
          <>
            <div className="space-y-4">
              {userDeviceLinks?.length > 0 ? (
                userDeviceLinks?.map((link, index) => {
                  const userWithRole = users?.find(
                    (user) => user?.id === link.userId,
                  );
                  return (
                    <div
                      key={index}
                      className="flex items-center justify-between"
                    >
                      <div>
                        <p className="text-sm leading-none font-medium">
                          {userWithRole?.userName}
                        </p>
                        <div>
                          {userWithRole?.aspNetUserRoles.map((role, index) => {
                            return (
                              <Badge key={index}> {role.aspNetRole.name}</Badge>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-muted-foreground text-sm font-medium">
                  No user assigned to this device.
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            <div>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between"
                  >
                    <span className="text-muted-foreground">Select user</span>
                    <ChevronsUpDown className="opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="p-0">
                  <Command>
                    <CommandInput placeholder="Select user" className="h-9" />
                    <CommandList>
                      <CommandEmpty>Not found.</CommandEmpty>
                      <CommandGroup>
                        {options.map((option) => (
                          <CommandItem
                            key={option.value}
                            // cmdk filters on `value`, so it must be the
                            // searchable username rather than the user id.
                            value={option.label}
                            onSelect={() => {
                              confirmAssignUserToDevice(option.value);
                              setOpen(false);
                            }}
                          >
                            {option.label}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>
            <Separator orientation="horizontal" />
            <div className="space-y-4">
              {userDeviceLinks?.map((link, index) => {
                const userWithRole = users?.find(
                  (user) => user?.id === link.userId,
                );
                return (
                  <div
                    key={index}
                    className="flex items-center justify-between"
                  >
                    <div className="space-y-1">
                      <p className="text-sm leading-none font-medium">
                        {userWithRole?.userName}
                      </p>
                      {userWithRole?.aspNetUserRoles.map((role, index) => {
                        return (
                          <Badge key={index}> {role.aspNetRole.name}</Badge>
                        );
                      })}
                    </div>

                    <div>
                      <Button
                        variant={'outline'}
                        size={'icon'}
                        type="button"
                        onClick={() => void removeUser(link.id)}
                      >
                        <X />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
